/** Reject a pending lifecycle request with a note (required — the backend answers
 *  `reason_required` for an empty one). The tenant stays as it is. A request decided meanwhile
 *  (409 `request_not_pending`) turns Reject off and re-reads the tab. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  LIFECYCLE_REASON_MAX,
  lifecycleApi,
  lifecycleError,
  lifecycleErrorWords,
} from '@/api/tenantLifecycle';
import type { TenantRequest } from '@/api/tenantRequests';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { LifecycleRequestQuote } from './lifecycleFormat';
import { useLifecycleRefresh } from './useLifecycleRefresh';

const Schema = z.object({
  note: z
    .string()
    .trim()
    .min(1, 'Say why — the requester reads this note.')
    .max(LIFECYCLE_REASON_MAX, `At most ${LIFECYCLE_REASON_MAX} characters.`),
});
type Values = z.infer<typeof Schema>;

interface Props {
  topId: number;
  /** The request to reject; null keeps the dialog closed. */
  request: TenantRequest | null;
  onClose: () => void;
}

export function RejectLifecycleDialog({ topId, request, onClose }: Props) {
  const snackbar = useSnackbar();
  const refresh = useLifecycleRefresh();
  const { register, handleSubmit, reset, setError, formState } = useForm<Values>({
    resolver: zodResolver(Schema),
    defaultValues: { note: '' },
  });

  const mut = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => lifecycleApi.reject(topId, id, note),
    onSuccess: () => {
      snackbar.show('Request rejected — the tenant stays as it is.', 'success');
      refresh();
      onClose();
    },
    onError: (e) => {
      const code = lifecycleError(e)?.code;
      if (code === 'reason_required') setError('note', { message: lifecycleErrorWords(e) });
      if (code === 'request_not_pending') refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (!request) return;
    reset({ note: '' });
    resetMutation();
  }, [request, reset, resetMutation]);

  const errorCode = lifecycleError(mut.error)?.code;
  const showError = mut.error != null && errorCode !== 'reason_required';
  const decidedMeanwhile = errorCode === 'request_not_pending';
  const submit = handleSubmit((v) => request && mut.mutate({ id: request.id, note: v.note }));

  return (
    <AppDialog
      open={request != null}
      onClose={onClose}
      title="Reject the request"
      disableBackdropClose={mut.isPending || formState.isDirty}
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={mut.isPending || decidedMeanwhile}
            onClick={() => void submit()}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Reject request
          </Button>
        </>
      }
    >
      {request && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <LifecycleRequestQuote request={request} />
          <Typography variant="body2" color="text.secondary">
            Nothing changes on the tenant. The note is kept with the request and in the audit
            record.
          </Typography>
          <TextField
            {...register('note')}
            label="Note"
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
            disabled={mut.isPending}
            inputProps={{ maxLength: LIFECYCLE_REASON_MAX }}
            error={Boolean(formState.errors.note)}
            helperText={formState.errors.note?.message ?? 'Why the request is turned down.'}
          />
          {showError && <Alert severity="error">{lifecycleErrorWords(mut.error)}</Alert>}
        </Box>
      )}
    </AppDialog>
  );
}
